
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check } from "lucide-react";

// Calculate the actual annual savings percentage between monthly and annual plan
const getSavingsPercent = (monthly: number, annual: number) => {
  const monthlyTotal = monthly * 12;
  const annualTotal = annual * 12;
  const savings = monthlyTotal - annualTotal;
  const percent = (savings / monthlyTotal) * 100;
  return Math.round(percent);
};

interface PricingCardProps {
  features: string[];
  className?: string;
  showSavingsBadge?: boolean;
  isAnnual?: boolean;
  onToggleIsAnnual?: (isAnnual: boolean) => void;
  savingsPercent?: number;
}

const PricingCard: React.FC<PricingCardProps> = ({
  features,
  className = '',
  showSavingsBadge = true,
  isAnnual: externalIsAnnual,
  onToggleIsAnnual,
  savingsPercent: externalSavingsPercent
}) => {
  // Use internal state if no external state is provided
  const [internalIsAnnual, setInternalIsAnnual] = useState(true);
  
  // Determine if we're using internal or external state
  const isAnnual = externalIsAnnual !== undefined ? externalIsAnnual : internalIsAnnual;
  const setIsAnnual = (value: boolean) => {
    if (onToggleIsAnnual) {
      onToggleIsAnnual(value);
    } else {
      setInternalIsAnnual(value);
    }
  };
  
  const monthlyPrice = 247;
  const annualPrice = 199;
  const yearlyTotal = 2388;
  const calculatedSavingsPercent = getSavingsPercent(monthlyPrice, annualPrice);
  const savingsPercent = externalSavingsPercent || calculatedSavingsPercent;

  const defaultFeatures = [
    "Real-time budget tracking",
    "Cross-channel media analytics",
    "AI-powered performance insights",
    "KPI tracking dashboard",
    "Automated reporting",
    "Media planning calendar",
    "Sales & ad spend forecasting",
    "Dedicated support & resources"
  ];

  const leftColumnFeatures = defaultFeatures.slice(0, 4);
  const rightColumnFeatures = defaultFeatures.slice(4);

  return (
    <Card className={`rounded-xl relative ${className}`}>
      {/* Badge for mobile view - only shown on small screens when in annual mode */}
      {isAnnual && showSavingsBadge && (
        <div
          className="lg:hidden absolute top-0 right-0 bg-green-500 text-white px-3 py-1 rounded-tr-xl rounded-bl-lg text-xs sm:text-sm sm:px-4 font-medium shadow-md z-10"
          style={{ pointerEvents: "none" }}
        >
          Save {savingsPercent}%
        </div>
      )}
      <CardHeader className="text-center">
        <CardTitle className="text-3xl md:text-4xl font-normal font-charter mb-6">
          One plan. All the features.
        </CardTitle>
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="relative bg-gray-100 rounded-full p-1">
            <div className="relative z-10 grid grid-cols-2 gap-1">
              <button
                onClick={() => setIsAnnual(true)}
                className={`px-6 py-2 text-sm font-medium transition-colors duration-200 rounded-full ${
                  isAnnual ? 'text-primary' : 'text-gray-500'
                }`}
              >
                Annually
              </button>
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-6 py-2 text-sm font-medium transition-colors duration-200 rounded-full ${
                  !isAnnual ? 'text-primary' : 'text-gray-500'
                }`}
              >
                Monthly
              </button>
            </div>
            <div
              className="absolute inset-y-1 w-[calc(50%-4px)] bg-white rounded-full shadow-sm transition-all duration-200 ease-in-out"
              style={{
                left: isAnnual ? '2px' : 'calc(50% + 2px)',
              }}
            />
          </div>
        </div>
        <CardDescription className="text-base sm:text-lg mt-2">
          {isAnnual ? 'Best value for long-term growth' : 'Perfect for growing businesses'}
        </CardDescription>
        <div className="mt-4">
          <span className="text-4xl font-bold">
            ${isAnnual ? annualPrice : monthlyPrice}
          </span>
          <span className="text-muted">/month</span>
        </div>
        <div className="text-sm text-muted mt-1">
          {isAnnual ? (
            `Billed $${yearlyTotal.toLocaleString()} yearly`
          ) : (
            <span className="invisible">Spacing placeholder</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-grow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div className="space-y-4">
            {leftColumnFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 list-none">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm sm:text-base">{feature}</span>
              </li>
            ))}
          </div>
          <div className="space-y-4">
            {rightColumnFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 list-none">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm sm:text-base">{feature}</span>
              </li>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter className="mt-auto pt-0 sm:pt-8 flex flex-col sm:flex-row justify-center gap-3">
        <button 
          className="px-8 bg-black text-white py-3 rounded-full hover:opacity-90 transition-opacity font-medium"
          onClick={() => window.location.href = 'https://app.plannifi.com/auth/sign-up'}
        >
          Get Started {isAnnual ? 'Annually' : 'Monthly'}
        </button>
        <button 
          className="px-8 bg-[#27B5FC] text-white py-3 rounded-full hover:opacity-90 transition-opacity font-medium"
          onClick={() => window.location.href = 'https://calendly.com/plannifi-demo/30min?back=1'}
        >
          Book a Free Demo
        </button>
      </CardFooter>
    </Card>
  );
};

export default PricingCard;
